import {
  type ThemeOptions,
  type ComponentsProps,
  type ComponentsOverrides,
  type ComponentsVariants,
  alpha
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

import { HeroVariants } from './Hero.types';
import { secondaryColors } from '../ThemeRegistry/theme';

interface LayoutConfig {
  [key: string]: { [breakpoint: string]: number };
}

function createLayoutConfig() {
  return {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 2,
    xl: 2
  };
}

export const layoutConfig: LayoutConfig = {
  [HeroVariants.mediaBelow]: createLayoutConfig(),
  [HeroVariants.mediaAbove]: createLayoutConfig(),
  [HeroVariants.mediaAboveFull]: createLayoutConfig(),
  [HeroVariants.mediaOnRight]: createLayoutConfig(),
  [HeroVariants.mediaOnRightFullBleed]: createLayoutConfig(),
  [HeroVariants.mediaOnLeft]: createLayoutConfig(),
  [HeroVariants.mediaOnLeftFullBleed]: createLayoutConfig()
};

const defaultProps: ComponentsProps['Hero'] = {
  variant: HeroVariants.default
};

const styleOverrides: ComponentsOverrides<Theme>['Hero'] = {
  root: ({ theme, ownerState }) => ({
    ...theme.mixins.applyBackgroundColor({ ownerState, theme }),
    containerType: 'inline-size',
    position: 'relative',
    zIndex: 2,
    overflow: 'hidden',
    display: 'flex'
  }),

  bottomContentWrap: ({ theme }) => ({
    '& > *': {
      [theme.breakpoints.up('md')]: {
        padding: '0 !important'
      }
    }
  }),

  contentOuterGrid: ({ theme }) => ({
    [theme.breakpoints.up('md')]: {
      minHeight: '20vh'
    }
  }),

  title: {
    whiteSpace: 'pre-line'
    // minHeight: '1.5em'
  },

  overline: ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      marginBottom: 0
    }
  }),

  content: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'justifyContent': 'center',

    [theme.breakpoints.up('md')]: {
      padding: 0,
      gap: 0
    },

    '> *:last-child': {
      marginBottom: 0
    }
  }),

  mainContentWrap: ({ theme, ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gridColumnStart: 'start',
    gridColumnEnd: 'end',
    paddingTop: 'var(--grid-gap-double)',
    paddingBottom: 'var(--grid-gap-double)',

    [theme.breakpoints.up('md')]: {
      paddingTop: 'var(--section-padding)',
      paddingBottom: 'var(--section-padding)',

      ...(!!ownerState?.showRainbowBar && {
        paddingLeft: 'var(--grid-gap)'
      }),

      ...(ownerState?.variant === HeroVariants.mediaBelow &&
        !!ownerState?.images?.length && {
          paddingBottom: 0
        })
    }
  }),

  mediaWrap: ({ ownerState, theme }) => ({
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'flexDirection': 'column',
    'boxShadow': theme.shadows['L'],
    'maxHeight': 'inherit',
    'gridColumnStart': 'start',
    'gridColumnEnd': 'end',
    'position': 'relative',

    ...((ownerState?.variant === HeroVariants.mediaAbove ||
      ownerState?.variant === HeroVariants.mediaAboveFull) && {
      [theme.breakpoints.up('lg')]: {
        paddingTop: 'var(--grid-gap-double)'
      }
    }),

    'picture': {
      display: 'flex',
      height: '100%',
      width: '100%',
      position: 'relative',

      [theme.breakpoints.down('lg')]: {
        maxHeight: '30vh'
      },

      ...((ownerState?.variant === HeroVariants.mediaAbove ||
        ownerState?.variant === HeroVariants.mediaBelow) && {
        maxHeight: '30vh',
        paddingBottom: 'var(--grid-gap-double)'
      })
    },

    '& :is(img, svg, video)': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 'var(--grid-gap)',

      ...(ownerState?.variant === HeroVariants.mediaOnRightFullBleed && {
        [theme.breakpoints.down('md')]: {
          borderRadius: 0
        },

        [theme.breakpoints.up('md')]: {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }
      }),

      ...(ownerState?.variant === HeroVariants.mediaOnLeftFullBleed && {
        [theme.breakpoints.down('md')]: {
          borderRadius: 0
        },

        [theme.breakpoints.up('md')]: {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }
      })
    },

    // '& :is(video)': {
    //   borderRadius: 'var(--grid-gap)'
    // },

    '& [class*=videoWrapper]': {
      height: 'auto',

      ...((ownerState?.variant === HeroVariants.mediaAbove ||
        ownerState?.variant === HeroVariants.mediaBelow) && {
        [theme.breakpoints.up('md')]: {
          height: 'auto',
          maxWidth: '50vw',
          margin: '0 auto'
        },

        [theme.breakpoints.up('xl')]: {
          height: 'auto',
          maxWidth: '30vw',
          margin: '0 auto'
        },

        ...(ownerState?.variant === HeroVariants.mediaAbove && {
          [theme.breakpoints.down('lg')]: {
            paddingTop: 'var(--grid-gap-double)'
          }
        }),

        ...(ownerState?.variant === HeroVariants.mediaBelow && {
          paddingBottom: 'var(--grid-gap-double)'
        })
      }),

      ...((ownerState?.variant === HeroVariants.mediaOnLeft ||
        ownerState?.variant === HeroVariants.mediaOnRight) && {
        [theme.breakpoints.down('md')]: {
          paddingBottom: 'var(--section-padding)'
        },

        [theme.breakpoints.up('md')]: {
          paddingTop: 'var(--section-padding)',
          paddingBottom: 'var(--section-padding)'
        }
      }),

      ...((ownerState?.variant === HeroVariants.mediaOnLeftFullBleed ||
        ownerState?.variant === HeroVariants.mediaOnRightFullBleed) && {
        [theme.breakpoints.up('md')]: {
          paddingTop: 'var(--section-padding)',
          paddingBottom: 'var(--section-padding)'
        }
      })
    },

    '.fade-wrap': {
      // display: 'contents',
      display: 'flex',
      alignItems: 'center'
    }

    // img: {
    //   width: '100%',
    //   objectFit: !!ownerState?.showFullImage ? 'contain' : 'cover'
    // }
  }),

  actionsWrap: ({ theme, ownerState }) => ({
    display: 'flex',
    gap: 'var(--grid-gap)',
    marginTop: 'var(--grid-gap)',
    flexWrap: 'wrap',

    [theme.breakpoints.only('md')]: {
      gridColumn: 'start/end'
    },

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    },

    ...((ownerState?.variant === HeroVariants.mediaAbove ||
      ownerState?.variant === HeroVariants.mediaBelow) && {
      justifySelf: 'center',
      alignSelf: 'center'
    })
  }),

  contentInnerWrap: {
    '& > *:last-child': {
      marginBottom: 0,
      paddingBottom: 0
    }
  },

  // scrollToContentWrap: ({ theme }) => ({
  //   'position': 'absolute',
  //   'zIndex': 100,
  //   'bottom': 0,

  //   '& > *': {
  //     gridColumn: 'start/end',
  //     justifySelf: 'flex-end',
  //     minHeight: '4em',
  //     display: 'flex',
  //     transform: 'translate(0, 50%)',
  //     marginBottom: 0,
  //     whiteSpace: 'noWrap',
  //     cursor: 'pointer',

  //     [theme.breakpoints.up('xl')]: {
  //       transform: 'translate(100%, 50%)'
  //     }
  //   }
  // }),

  rainbowBar: ({ theme, ownerState }) => ({
    position: 'absolute',
    width: '0.5rem',
    height: '100%',
    left: `calc(0px - 1rem)`,
    background: `linear-gradient(to bottom, ${secondaryColors.red} 0%, ${secondaryColors.red} 25%, var(--mui-palette-schemes-orange-primary-main) 25%, var(--mui-palette-schemes-orange-primary-main) 50%, var(--mui-palette-schemes-green-primary-main) 50%, var(--mui-palette-schemes-green-primary-main) 75%, var(--mui-palette-schemes-blue-primary-main) 75%, var(--mui-palette-schemes-blue-primary-main) 100%)`
  }),

  tabletTitleWrap: ({ theme, ownerState }) => ({
    ...((ownerState?.variant === HeroVariants.mediaAbove ||
      ownerState?.variant === HeroVariants.mediaAboveFull ||
      ownerState?.variant === HeroVariants.mediaBelow) && {
      justifySelf: 'center',
      alignSelf: 'center',
      textAlign: 'center'
    })
  }),

  rainbowBarWrap: ({ theme, ownerState }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)',
    justifyContent: 'center',
    minHeight: 'var(--grid-gap-quad)',
    marginBottom: 'var(--grid-gap-half)',

    [theme.breakpoints.only('md')]: {
      alignSelf: 'center',

      ...(ownerState?.variant === HeroVariants.mediaOnLeft && {
        gridColumn: 'half'
      })
    },

    ...(ownerState?.variant === HeroVariants.mediaOnRightFullBleed && {
      gridColumn: 'start/half',
      gridRow: 1
    })
  })
};

const createVariants = (theme: Theme): ComponentsVariants['Hero'] => [
  {
    props: {
      variant: HeroVariants.mediaOnRight
    },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 1,
        gridColumnStart: 'start',
        gridColumnEnd: 'end',

        [theme.breakpoints.up('md')]: {
          gridColumnEnd: 'half'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 2,
        gridColumnStart: 'start',
        gridColumnEnd: 'end',

        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnStart: 'half',
          alignItems: 'flex-end'
        }
      },

      'picture :is(video, img, svg)': {
        objectFit: 'contain'
      }
    }
  },
  {
    props: {
      variant: HeroVariants.mediaOnRightFullBleed
    },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 1,

        gridColumnStart: 'start',
        gridColumnEnd: 'end',

        [theme.breakpoints.up('md')]: {
          gridColumnEnd: 'half'
        },

        [theme.breakpoints.up('xl')]: {
          gridRow: 1,
          gridColumnEnd: 'five-end'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 2,
        gridColumnStart: 'full-start',
        gridColumnEnd: 'full-end',
        padding: 0,
        height: '100%',

        [theme.breakpoints.up('md')]: {
          gridRow: '1/-1',
          gridColumnStart: 'half',
          alignItems: 'flex-end'
        }
      }
    }
  },
  {
    props: {
      variant: HeroVariants.mediaOnLeft
    },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 1,
        gridColumnStart: 'start',

        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnStart: 'half'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 2,
        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnEnd: 'half',
          alignItems: 'flex-start'
        }
      }
    }
  },
  {
    props: {
      variant: HeroVariants.mediaOnLeftFullBleed
    },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 2,

        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnStart: 'eight-start'
        }
      },

      '[class*=mediaWrap]': {
        gridColumnStart: 'full-start',
        gridColumnEnd: 'full-end',
        padding: 0,
        height: '100%',

        [theme.breakpoints.up('md')]: {
          gridColumnEnd: 'half',
          alignItems: 'flex-start'
        }
      },

      'picture': {
        position: 'absolute'
      }
    }
  },

  {
    props: {
      variant: HeroVariants.mediaAbove
    },
    style: {
      '[class*=mainContentWrap]': {
        'gridRow': 2,

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        },

        '& *': {
          alignSelf: 'center',
          textAlign: 'center'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 1
      }
    }
  },

  {
    props: {
      variant: HeroVariants.mediaAboveFull
    },
    style: {
      '[class*=mainContentWrap]': {
        'gridRow': 2,

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        },

        '& *': {
          alignSelf: 'center',
          textAlign: 'center'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 1
      }
    }
  },
  {
    props: {
      variant: HeroVariants.mediaBelow
    },
    style: {
      '[class*=mainContentWrap]': {
        'gridRow': 1,
        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        },

        '& *': {
          alignSelf: 'center',
          textAlign: 'center'
        }
      },

      '[class*=mediaWrap]': {
        gridRow: 2
      }
    }
  }
];

export const heroTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Hero: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default heroTheme;
